import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Check, X, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AmazonAdminPanel = ({ onBack, user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/amazon/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      setError("Failed to load users");
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/amazon/admin/approve`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User ${email} approved successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleReject = async (email) => {
    if (!window.confirm(`Are you sure you want to reject ${email}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/amazon/admin/reject`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User ${email} rejected`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reject user");
    }
  };

  const handleToggleAdmin = async (email, currentRole) => {
    const newRole = currentRole === 'amazon_admin' ? 'amazon' : 'amazon_admin';
    const action = newRole === 'amazon_admin' ? 'promote to admin' : 'demote to user';
    
    if (!window.confirm(`Are you sure you want to ${action} ${email}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/amazon/admin/set-role`, { email, role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User ${email} ${action === 'promote to admin' ? 'promoted' : 'demoted'} successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update user role");
    }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');
  const activeUsers = users.filter(u => u.status === 'active');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'amazon_admin') {
      return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">User</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Admin Panel</h1>
          <p className="text-slate-600">Manage Amazon Portal users</p>
        </div>

        {/* Messages */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        {/* Pending Users */}
        {pendingUsers.length > 0 && (
          <Card className="mb-6 border-yellow-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                Pending Approvals ({pendingUsers.length})
              </CardTitle>
              <CardDescription>Users waiting for approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((u) => (
                  <div key={u.email} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="font-medium">{u.first_name} {u.last_name}</p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                      <p className="text-xs text-slate-400">Position: {u.position} | Site: {u.site_postcode}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(u.email)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(u.email)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Users */}
        <Card>
          <CardHeader>
            <CardTitle>Active Users ({activeUsers.length})</CardTitle>
            <CardDescription>Approved Amazon Portal users</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-500 text-center py-4">Loading users...</p>
            ) : activeUsers.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No active users yet</p>
            ) : (
              <div className="space-y-3">
                {activeUsers.map((u) => (
                  <div key={u.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{u.first_name} {u.last_name}</p>
                          {getRoleBadge(u.role)}
                        </div>
                        <p className="text-sm text-slate-500">{u.email}</p>
                        <p className="text-xs text-slate-400">Position: {u.position} | Site: {u.site_postcode}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {(user?.role === 'admin' || user?.role === 'zkteco_admin') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleAdmin(u.email, u.role)}
                        >
                          {u.role === 'amazon_admin' ? 'Demote' : 'Make Admin'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AmazonAdminPanel;
